import * as forgerock from '@forgerock/javascript-sdk';
import { WebAuthnStepType, OAuth2Client, TokenStorage } from '@forgerock/javascript-sdk'; //Passkey POC
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { detectIncognito } from 'detectincognitojs';
import { Exception } from 'sass';
import { useAdobeLaunch } from '../Common/utils/adobeLaunch';
import { setCustomerProfileDataFromTokens, setTokenToSession } from './LoginBase';
import { extendSession_IB, ticketExtendSession_IB } from './BetSlipIBAPI';
import { isLogonHigh } from './LoginIBHooks';

const FATAL = 'Fatal';

// Passkey POC
const isPOC = new URL(window.location.href).searchParams.has('hkjcWebAuthnReg');

const tempConfig = {
    AM_URL: window.globalConfig.ARKLE_URL,
    REALM_PATH: 'customer',
    SCOPE: window.globalConfig.arkleTokenScope || 'hs_txn_session openid',
    // openid ls_bet.profile ls_profile
    TIMEOUT: window.globalConfig.LOGIN_TIMEOUT_DURATION,
    TREE: isPOC ? 'hkjcWebAuthnReg' : 'hkjcHSLogin-Web', // Passkey POC
    WEB_OAUTH_CLIENT: window.globalConfig.IS_SPEEDBET ? 'SPEEDBET' : 'JCBW',
    NX_NO_CLOUD: 'true'
};

const addHeadersMiddleware = (req, action, next) => {
    switch (action.type) {
        case 'START_AUTHENTICATE':
            req.init.headers.append('Channel-Id', window.globalConfig.IS_SPEEDBET ? 'SPEEDBET' : 'JCBW');
            break;
    }

    if (req.init.headers && window.globalConfig.useOTPPrefix) {
        req.init.headers.append('API-Version', 3);
    } else if (req.init.headers && window.globalConfig.useVoiceOTP) {
        req.init.headers.append('API-Version', 2);
    }

    if (req.init.headers && localStorage.getItem('app-trace-id') && window.globalConfig.useSplunkLog) {
        req.init.headers.append('app-trace-name', 'JCBW');
        req.init.headers.append('app-trace-id', localStorage.getItem('app-trace-id'));
    }

    const ArkleStage = window.sessionStorage.getItem('ArkleStage');
    if (ArkleStage) {
        req.url.searchParams.set('stage', ArkleStage);
    }

    next();
};

forgerock.Config.set({
    clientId: tempConfig.WEB_OAUTH_CLIENT,
    middleware: [addHeadersMiddleware],
    redirectUri: `${window.location.origin}/callback`,
    scope: tempConfig.SCOPE,
    serverConfig: {
        baseUrl: tempConfig.AM_URL,
        timeout: parseInt(tempConfig.TIMEOUT)
    },
    realmPath: tempConfig.REALM_PATH,
    tree: tempConfig.TREE,
    type: 'code'
});

window.refreshSessionIntervalId = undefined;

export const useLoginLogout_FR = (props) => {
    const { t, i18n } = useTranslation();
    const [isShowOTPAuth, setIsShowOTPAuth] = useState(false);
    const [isShowSecurityPinAuth, setIsShowSecurityPinAuth] = useState(false);
    const [isShowOTPToast, setIsShowOTPToast] = useState(false);
    const [invalidOTP, setInvalidOTP] = useState(false);
    const [invalidPIN, setInvalidPIN] = useState(false);
    const [currentStep, setCurrentStep] = useState();
    const [isShowTrustBrowser, setIsShowTrustBrowser] = useState(false);
    const [jwtToken, setJwtToken] = useState('');
    const [mobile, setMobile] = useState('');
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [showLoginLockPopup, setShowLoginLockPopup] = useState(false);
    const [isIncognito, setIsIncognito] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isMissingMobile, setIsMissingMobile] = useState(false);
    const [isVoiceOTP, setIsVoiceOTP] = useState(false);
    const [isSecurityPin, setIsSecurityPin] = useState(false);
    const [isPassKeyRegConsent, setIsPassKeyRegConsent] = useState(false);
    const [isPassKeyRegSuccess, setIsPassKeyRegSuccess] = useState(false);
    const { sendAdobeEvent } = useAdobeLaunch();

    const {
        pwd,
        setPwd,
        loginIncorrectMsg,
        setLoginIncorrectMsg,
        closeLoginIncorrectMsg,
        checkAccountPwd,
        checkNewCIAMJwt,
        setLoginAPILoading,
        getEwinSessionID,
        latitude,
        setLatitude,
        longitude,
        setLongitude
    } = props;

    useEffect(() => {
        detectIncognito().then((result) => {
            console.debug(result.browserName, result.isPrivate);
            setIsIncognito(result.isPrivate);
        });
    }, []);

    const next = async (step) => {
        try {
            if (step != null) {
                window.sessionStorage.setItem('ArkleStage', step?.getStage());
            } else {
                window.sessionStorage.removeItem('ArkleStage');
            }

            const rsp = await forgerock.FRAuth.next(step);
            if (window.showException == true) {
                window.showException = false;
                throw new Exception('test exception');
            }
            if (rsp === undefined) {
                //cant get rsp
                throw new Exception('NetworkError');
            } else {
                return rsp;
            }
        } catch (error) {
            if (error.message.includes('NetworkError') || error.response) {
                console.error('Network error: Please check your internet connection.');
                setShowErrorPopup('network');
            } else if (
                (error instanceof TypeError && error.message === 'Failed to fetch') ||
                error.message === 'Load failed'
            ) {
                // Handle network connection lost error here
                console.error('Network connection lost. Please check your network settings.');
                setShowErrorPopup('network');
            } else {
                console.log('Error in next', error);
                setShowErrorPopup('exception');
            }
        }
    };

    // step 1 init
    const FR_initStep = async () => {
        try {
            let step = await next(null);
            if (step?.payload?.status != '200' && step?.type == 'LoginFailure') {
                // setIsShowTrustBrowser(false);
                console.log('Error in next', step);
                setShowErrorPopup('exception');
                return;
            }
            const stage = step?.getStage();
            switch (stage) {
                case 'pre-namepass':
                    step = await getPreNamepass(step);
                    break;
                case 'otp':
                    break;
                case 'securitypin-callback':
                    break;
                case 'trust-browser-confirm':
                    break;
            }

            setCurrentStep(step);

            return step;
        } catch (error) {
            console.log('Error in init', error);
            await forgerock.FRUser.logout(); //force logout so customer can try again
        }
    };

    const getPreNamepass = async (step) => {
        const choiceCallback = step.getCallbackOfType('ChoiceCallback');
        //0 sso, customer always need to enter password so always set choice index to 1
        //if(sessionStorage.getItem('sso_web_id') && sessionStorage.getItem('sso_web_id').length > 0) {
        //    choiceCallback.setChoiceIndex(0);
        //} else {
        choiceCallback.setChoiceIndex(1);
        //}

        const rsp = await next(step);

        return rsp;
    };

    function success(pos) {
        const crd = pos.coords;
        setLatitude(crd.latitude);
        setLongitude(crd.longitude);
        //console.log(`Arkle Latitude : ${crd.latitude}`);
        //console.log(`Arkle Longitude: ${crd.longitude}`);
    }

    function error(err) {
        console.warn(`Arkle Geo Error (${err.code}): ${err.message}`);
    }

    // step 2 login with user name and password
    const FR_loginWithUsernamePassword = async (username, pwd) => {
        // // test function

        // for testing convenience only
        await FR_Logout();
        let step = await FR_initStep();

        //let step = currentStep;
        let nextStep = step; //currentStep;

        try {
            if (step?.payload?.status == '200' && step?.payload.stage == 'namepass') {
                const nameCallback = step.getCallbackOfType('NameCallback');
                const pwdCallback = step.getCallbackOfType('PasswordCallback');

                nameCallback.setName(username);
                pwdCallback.setPassword(pwd);
                nextStep = await next(step); //await forgerock.FRAuth.next(step);
            }

            // Passkey POC
            if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'passkey-bind-process') {
                console.debug('passkey-bind-process');
                const stepType = forgerock.FRWebAuthn.getWebAuthnStepType(nextStep);

                console.debug('stepType', stepType);
                if (stepType === WebAuthnStepType.Registration) {
                    // Registering a new device, with optional device name
                    console.debug('Registering a new device');
                    step = await forgerock.FRWebAuthn.register(nextStep, 'myDeviceName');
                    console.debug('passkey saved', step);
                    nextStep = await next(step);
                    console.debug('done register', nextStep);
                    await FR_passkeyAuthenticate(nextStep);
                }
            } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'passkey-sign-process') {
                await FR_passkeyAuthenticate(nextStep);
                //Passkey POC
            } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'otp') {
                const otpCallback = JSON.parse(nextStep.getCallbackOfType('TextOutputCallback').getMessage());
                setMobile({
                    mobileNo: otpCallback.mobile,
                    timeout: otpCallback.timeout,
                    prefixCode: otpCallback.prefixCode
                });
                closeLoginIncorrectMsg();
                setIsShowOTPAuth(true);
                sendAdobeEvent({
                    event: 'interaction',
                    interaction: { eventID: 'eWin_CIAM_S1a-OTP_whitelistWithMobile' }
                });
                window.sessionStorage.setItem('LoginWA', sessionStorage.getItem('LoginWA') + 'WithMobile');
                step = nextStep;
                //ask for geo location
                const options = {
                    timeout: 1000
                };
                navigator.geolocation.getCurrentPosition(success, error, options);
            } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'securitypin-callback') {
                // FR_loginWithPIN('', nextStep)
                setIsShowSecurityPinAuth(true);
                setIsSecurityPin(true);
                step = nextStep;
            } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'passkey-reg-consent') {
                // } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'passkey-bind-process') {
                setIsPassKeyRegConsent(true);
            } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'trust-browser-confirm') {
                setIsShowTrustBrowser(true);
                setIsShowOTPAuth(false);
                setIsShowSecurityPinAuth(false);
                setCIAMLoginIncorrectMsg('');
                //step = nextStep;
            } else if (nextStep?.payload.stage == 'passkey-reg-success') {
                setCIAMLoginIncorrectMsg('');
                setIsPassKeyRegConsent(false); // hide otp modal if login success
                setIsPassKeyRegSuccess(true);
                // step = nextStep;

                // await FR_LoginSuccess(step);
            } else if (nextStep?.type == 'LoginSuccess') {
                setIsShowTrustBrowser(false);
                setCIAMLoginIncorrectMsg('');
                setIsShowOTPAuth(false);
                setIsShowSecurityPinAuth(false);
                setCurrentStep(nextStep);
                await FR_LoginSuccess(nextStep);
            } else if (nextStep?.payload?.status != '200' && nextStep?.type == 'LoginFailure') {
                setIsShowTrustBrowser(false);
                setCIAMLoginIncorrectMsg('100103');
                await FR_Logout().then(FR_initStep());
            } else {
                if (!nextStep?.getCallbacksOfType('TextOutputCallback')?.length) {
                    return;
                }

                const rawMessage = nextStep?.getCallbackOfType('TextOutputCallback')?.getMessage();
                let message = null;
                try {
                    message = JSON.parse(nextStep?.getCallbackOfType('TextOutputCallback')?.getMessage());
                } catch (e) {}
                const result = message ? message.messageId?.replace('ERR', '') : rawMessage;

                await FR_Logout();
                await FR_initStep();
                switch (result) {
                    case '100011': //No mobile number available
                        if (
                            window.globalConfig.NEW_CIAM == 2 ||
                            (window.globalConfig.NEW_CIAM == 1 && sessionStorage.getItem('cutoverLogin') == 1)
                        ) {
                            setIsMissingMobile(true);
                            break;
                        }

                    case '100008': //Not associated with a betting account
                        setCIAMLoginIncorrectMsg(result);
                        break;
                    case '100103': //blacklisted account
                        setCIAMLoginIncorrectMsg('100103');
                        setIsShowOTPAuth(false);
                        setIsShowSecurityPinAuth(false);
                        break;
                    case '100014': //locked account
                        setCIAMLoginIncorrectMsg('100014');
                        setIsShowOTPAuth(false);
                        setIsShowSecurityPinAuth(false);
                        break;
                    case '100015': //not speedbet customer
                        setCIAMLoginIncorrectMsg('100015');
                        setIsShowOTPAuth(false);
                        setIsShowSecurityPinAuth(false);
                        break;
                    case '100017': //locked account
                        setCIAMLoginIncorrectMsg('100017');
                        setIsShowOTPAuth(false);
                        setIsShowSecurityPinAuth(false);
                        break;
                    case '100021': //SMS Limit Reached
                        setCIAMLoginIncorrectMsg('100021');
                        setIsShowOTPAuth(false);
                        setIsShowSecurityPinAuth(false);
                        setPwd?.('');
                        break;
                    case '100003': //invalid sso
                        return 'logout';
                    default:
                        setCIAMLoginIncorrectMsg(result);
                        setPwd?.('');
                        break;
                }
            }

            setCurrentStep(step);
        } catch (err) {
            console.log('exception', err, showErrorPopup);
            if (showErrorPopup != true) {
                //if already show error popup, no need to show another error
                if (err.message === 'Timeout') {
                    setLoginIncorrectMsg('TIMEOUT');
                } else {
                    setIsShowTrustBrowser(false);
                    setCIAMLoginIncorrectMsg('100103');
                    console.error(err);
                }
            }
        }
    };

    //passkey POC
    const FR_passkeyAuthenticate = async (nextStep) => {
        console.debug('passkey-sign-process');
        const stepType = forgerock.FRWebAuthn.getWebAuthnStepType(nextStep);
        console.debug('stepType', stepType);
        if (stepType === WebAuthnStepType.Authentication) {
            // Authenticating with a registered device
            console.debug('Authenticating with a registered device');
            const step = await forgerock.FRWebAuthn.authenticate(nextStep);
            console.debug('done authenticate', step);
            nextStep = await next(step);
            await FR_LoginSuccess(nextStep);
        }
    };

    //passkey POC
    const FR_passKeySuccess = async () => {
        let step = currentStep;
        if (step?.payload.stage == 'passkey-reg-success') {
            const textInputCallback = step.getCallbackOfType('ConfirmationCallback');
            textInputCallback.setInputValue(0);
            let nextStep = await next(step);
            setCurrentStep(nextStep);

            await FR_LoginSuccess(nextStep);
        }
    };

    //passkey POC
    const FR_passKeyBindProcess = async (isBindPassKey) => {
        let step = currentStep;

        let nextStep = step;

        if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'passkey-reg-consent') {
            const textInputCallback = step.getCallbackOfType('ConfirmationCallback');
            textInputCallback.setInputValue(isBindPassKey ? '0' : '1');
            nextStep = await next(step);
            setCurrentStep(nextStep);
        }

        if (nextStep?.payload.stage == 'passkey-reg-success') {
            setIsShowTrustBrowser(false);
            setIsPassKeyRegConsent(false);
            setIsPassKeyRegSuccess(true);
            await FR_passkeyAuthenticate(nextStep);
        }

        if (nextStep?.type == 'LoginSuccess') {
            setIsShowTrustBrowser(false);
            setIsPassKeyRegConsent(false);
            setCIAMLoginIncorrectMsg('');
            step = nextStep;

            await FR_LoginSuccess(step);
        } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'passkey-bind-process') {
            console.debug('passkey-bind-process');
            const stepType = forgerock.FRWebAuthn.getWebAuthnStepType(nextStep);

            console.debug('stepType', stepType);
            if (stepType === WebAuthnStepType.Registration) {
                // Registering a new device, with optional device name

                try {
                    step = (await forgerock.FRWebAuthn.register) < 'mydevice' > (step, 'mydevice');
                    // ensure the step here has the 'mydevice' name at the end of the value. (outcome)
                    const deviceValue = step.getCallbacksOfType('HiddenValueCallback')[0].getInputValue();
                    if (!deviceValue.includes('mydevice')) {
                        throw new Error('Device name is not correct');
                    }
                } catch (err) {
                    console.log(err);
                }
                // console.log('Registering a new device');
                // step = await forgerock.FRWebAuthn.register(nextStep, 'myDeviceName');
                // console.log("passkey saved", step);
                nextStep = await next(step);
                console.debug('done register', nextStep);
                // await FR_passkeyAuthenticate(nextStep);

                if (nextStep?.payload.stage == 'passkey-reg-success') {
                    setIsShowTrustBrowser(false);
                    setIsPassKeyRegConsent(false);
                    setIsPassKeyRegSuccess(true);
                    await FR_passkeyAuthenticate(nextStep);
                    setCurrentStep(nextStep);
                }
            }
        } else {
            const result = nextStep.getCode();
            const message = nextStep.getMessage();
            setIsPassKeyRegConsent(false);
            setCIAMLoginIncorrectMsg('ERROR');
            console.log(message);
            setPwd?.('');
        }
    };

    // step 3 login with PIN
    const FR_loginWithPIN = async (pin) => {
        // pin = '123456'
        let step = currentStep;
        let message = '';
        // console.log(step,'curstep')
        try {
            const nameCallback = step.getCallbackOfType('NameCallback');
            nameCallback.setInputValue(pin);
            const rsp = await next(step);
            if (rsp?.payload?.status == '200' && rsp?.type == 'LoginSuccess') {
                setIsShowTrustBrowser(false);
                setCIAMLoginIncorrectMsg('');
                setIsShowSecurityPinAuth(false);
                setCurrentStep(rsp);
                await FR_LoginSuccess(rsp);
            } else if (rsp?.type == 'LoginFailure') {
                switch (rsp?.payload.detail.errorCode) {
                    case '110':
                        setShowErrorPopup('session timeout|110');
                        break;
                    default:
                        setShowErrorPopup('otp failure|' + rsp?.payload.detail.errorCode);
                        break;
                }
            } else {
                switch (rsp?.payload.stage) {
                    case 'errorinfo':
                        let item = rsp.callbacks?.find((element) => {
                            if (element.payload.type == 'TextOutputCallback') {
                                return true;
                            }
                        });
                        message = JSON.parse(item.payload.output[0].value);

                        //await forgerock.FRAuth.next(step);
                        setInvalidPIN(message?.messageId);
                        //handle resend otp
                        if (message?.messageId == '100016') {
                            const textInputCallback = rsp.getCallbackOfType('ConfirmationCallback');
                            textInputCallback.setInputValue(1);
                            let step = await next(rsp);
                            setCurrentStep(step);
                        }
                        //await next(step);
                        // step = await next(step); //await forgerock.FRAuth.next(rsp);
                        break;
                    case 'trust-browser-confirm':
                        await handleConfirmTrustBrowser(rsp);
                        // setCurrentStep(rsp);
                        // setIsShowOTPAuth(false);
                        // setIsShowTrustBrowser(true);
                        // WATrackerTrackClickEvent('eWin_CIAM_S2-Trust-overlay_whitelistWithMobile');
                        break;
                    case 'passkey-reg-consent':
                        setCurrentStep(rsp);
                        setIsShowSecurityPinAuth(false);
                        setIsPassKeyRegConsent(true);
                        break;
                    default:
                        setIsShowTrustBrowser(false);
                        break;
                }
            }
        } catch (err) {
            console.log(err, 'err');
        }
    };

    // step 3 login with OTP
    const FR_loginWithOTP = async (otp) => {
        // const step = await setNextStep({otp: otp})
        let step = currentStep;
        let message = '';
        try {
            const textInputCallback = step.getCallbackOfType('TextInputCallback');
            textInputCallback.setInputValue(otp);
            const rsp = await next(step); //await forgerock.FRAuth.next(step);

            // setIsShowOTPAuth(false);
            // setIsPassKeyRegConsent(true);
            if (rsp?.payload?.status == '200' && rsp?.type == 'LoginSuccess') {
                setIsShowTrustBrowser(false);
                setCIAMLoginIncorrectMsg('');
                setIsShowOTPAuth(false);
                setCurrentStep(rsp);
                await FR_LoginSuccess(rsp);
            } else if (rsp?.type == 'LoginFailure') {
                switch (rsp?.payload.detail.errorCode) {
                    case '110':
                        setShowErrorPopup('session timeout|110');
                        break;
                    default:
                        setShowErrorPopup('otp failure|' + rsp?.payload.detail.errorCode);
                        break;
                }
            } else {
                switch (rsp?.payload.stage) {
                    case 'errorinfo':
                        message = JSON.parse(rsp.getCallbackOfType('TextOutputCallback').getMessage());
                        console.debug('message', message);
                        //await forgerock.FRAuth.next(step);
                        setInvalidOTP(message?.messageId);
                        //handle resend otp
                        if (message?.messageId == '100004' || message?.messageId == '100010') {
                            const textInputCallback = rsp.getCallbackOfType('ConfirmationCallback');
                            textInputCallback.setInputValue(1);
                            let step = await next(rsp);
                            setCurrentStep(step);
                        }
                        //await next(step);
                        // step = await next(step); //await forgerock.FRAuth.next(rsp);
                        break;
                    case 'trust-browser-confirm':
                        await handleConfirmTrustBrowser(rsp);
                        // setCurrentStep(rsp);
                        // setIsShowOTPAuth(false);
                        // setIsShowTrustBrowser(true);
                        // WATrackerTrackClickEvent('eWin_CIAM_S2-Trust-overlay_whitelistWithMobile');
                        break;
                    case 'passkey-reg-consent':
                        setCurrentStep(rsp);
                        setIsShowOTPAuth(false);
                        setIsPassKeyRegConsent(true);
                        break;
                    default:
                        //setIsShowOTPAuth(false);
                        setIsShowTrustBrowser(false);
                        break;
                }
            }
        } catch (err) {
            console.error(err);
        }

        return message;
    };

    const handleConfirmTrustBrowser = async (step) => {
        if (isIncognito) {
            console.debug('isIncognito', isIncognito);
            window.sessionStorage.setItem('LoginWA', sessionStorage.getItem('LoginWA') + '_Incognito');
            await FR_TurstBrowser(false, step).then(() => {
                setIsShowOTPAuth(false);
                setIsShowSecurityPinAuth(false);
                setIsShowTrustBrowser(false);
            });
        } else {
            if (step.payload.stage == 'trust-browser-confirm') {
                setCurrentStep(step);
                setIsShowOTPAuth(false);
                setIsShowSecurityPinAuth(false);
                setIsShowTrustBrowser(true);
                if (
                    !!window.sessionStorage.getItem('LoginWA') &&
                    window.sessionStorage.getItem('LoginWA').includes('WithMobile')
                ) {
                    sendAdobeEvent({
                        event: 'interaction',
                        interaction: {
                            eventID: `eWin_CIAM_S2-Trust-overlay_whitelistWithMobile${isVoiceOTP ? '-VoiceOTP' : isSecurityPin ? '-PIN' : ''}`
                        }
                    });
                } else if (window.sessionStorage.getItem('LoginWA').includes('WithoutMobile')) {
                    sendAdobeEvent({
                        event: 'interaction',
                        interaction: { eventID: 'eWin_CIAM_S2-Trust-overlay_whitelistWithoutMobile' }
                    });
                }
            } else {
                if (step.payload.stage == 'errorinfo') {
                    const message = JSON.parse(step.getCallbackOfType('TextOutputCallback').getMessage());
                    setShowErrorPopup(message?.messageId + '|' + message?.messageId);
                } else {
                    setShowErrorPopup('exception');
                }
            }
        }
    };

    // step 4 turst browser
    const FR_TurstBrowser = async (trust, preStep = null) => {
        try {
            let step = preStep ? preStep : currentStep;
            const textInputCallback = step.getCallbackOfType('ConfirmationCallback');
            textInputCallback.setInputValue(trust ? 0 : 1);

            const rsp = await next(step);

            if (rsp?.payload?.status == 200) {
                setCIAMLoginIncorrectMsg('');

                if (rsp?.type == 'LoginSuccess') {
                    await FR_LoginSuccess(rsp);
                    setIsShowTrustBrowser(false);
                } else {
                    await FR_Logout().then(FR_initStep);
                }
            } else if (rsp?.type == 'LoginFailure') {
                switch (rsp?.payload.detail?.errorCode) {
                    case '110':
                        setShowErrorPopup('session timeout|110');
                        break;
                    default:
                        setShowErrorPopup('trust browser failure|' + rsp?.payload.detail?.errorCode);
                        setIsShowTrustBrowser(false);
                        break;
                }
            } else if (rsp == undefined) {
                //network error return null as next will handle
                return null;
            } else {
                setShowErrorPopup('trust browser failure');
            }

            setCurrentStep(rsp);
            console.debug('rsp', rsp);
            return rsp;
        } catch (err) {
            console.log('trused browser err', err);
            return null;
        }
    };

    const FR_confirm_language = async (s, isShowOTPToast = true) => {
        try {
            const confirmationCallback = s.getCallbackOfType('ConfirmationCallback');
            confirmationCallback.setOptionIndex(1);
            const nextStep = await next(s);
            if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'otp') {
                const otpCallback = JSON.parse(nextStep.getCallbackOfType('TextOutputCallback').getMessage());
                setMobile({
                    mobileNo: otpCallback.mobile,
                    timeout: otpCallback.timeout,
                    prefixCode: otpCallback.prefixCode
                });
                closeLoginIncorrectMsg();
                setIsShowOTPAuth(true);
                if (isShowOTPToast) {
                    setIsShowOTPToast(true);
                }
                setCurrentStep(nextStep);
            } else if (nextStep?.type == 'LoginFailure') {
                switch (nextStep?.payload.detail.errorCode) {
                    case '110':
                        setShowErrorPopup('session timeout|110');
                        break;
                    default:
                        setShowErrorPopup('otp resend failure|' + nextStep?.payload.detail.errorCode);
                        break;
                }
                setCurrentStep(nextStep);
            }
        } catch (err) {
            console.log('Err:', err);
            setShowErrorPopup('session timeout');
            return null;
        }
    };

    const FR_ResendOTP = async (number = 1, isShowOTPToast = true) => {
        try {
            let step = currentStep;

            //"ConfirmationCallback"

            const confirmationCallback = step.getCallbackOfType('ConfirmationCallback');
            // confirmationCallback.setOptionIndex(number);
            const options = confirmationCallback.getOptions();
            confirmationCallback.setOptionValue(options?.[number]);
            const nextStep = await next(step);

            if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'otp') {
                const otpCallback = JSON.parse(nextStep.getCallbackOfType('TextOutputCallback').getMessage());
                setMobile({
                    mobileNo: otpCallback.mobile,
                    timeout: otpCallback.timeout,
                    prefixCode: otpCallback.prefixCode
                });
                closeLoginIncorrectMsg();
                setIsShowOTPAuth(true);

                if (isShowOTPToast) {
                    setIsShowOTPToast(true);
                }

                step = nextStep;
                setCurrentStep(step);
            } else if (nextStep?.payload?.status == '200' && nextStep?.payload.stage == 'voiceLanguage') {
                await FR_confirm_language(nextStep, isShowOTPToast);
            } else if (nextStep?.type == 'LoginFailure') {
                switch (nextStep?.payload.detail.errorCode) {
                    case '110':
                        setShowErrorPopup('session timeout|110');
                        break;
                    default:
                        setShowErrorPopup('otp resend failure|' + nextStep?.payload.detail.errorCode);
                        break;
                }
                step = nextStep;
                setCurrentStep(step);
            } else if (nextStep?.payload?.status == '200' && nextStep.payload.stage == 'errorinfo') {
                const message = JSON.parse(nextStep.getCallbackOfType('TextOutputCallback').getMessage());
                setShowErrorPopup(message?.messageId + '|' + message?.messageId);
            }
            return nextStep?.payload?.stage;
        } catch (err) {
            console.log('Err:', err);
            setShowErrorPopup('session timeout');
            return null;
        }
    };

    const FR_LoginSuccess = async (step) => {
        //remove as login stage completed
        window.sessionStorage.removeItem('ArkleStage');

        const token = step.getSessionToken();
        const accessTokens = await refreshToken(token);

        // Then refresh Arkle token + SSO cookie + EWIN session every 10 minutes
        if (!window.refreshSessionIntervalId) {
            window.refreshSessionIntervalId = setInterval(
                () => {
                    if (!isLogonHigh()) {
                        clearInterval(window.refreshSessionIntervalId);
                        window.refreshSessionIntervalId = undefined;

                        return;
                    }

                    refreshToken();
                    if (window.globalConfig.NEW_LOGOUT_REMINDER_FLOW) {
                        ticketExtendSession_IB();

                        // avoid calling extend session when in ES page
                        // ES page will keep pooling GetOpenBets, which may have seq no. clash with extend session calls
                        // and only poll extend session when betting account is defined (i.e. still logged in)
                        if (!/^\/([^/]+)\/football\/cashout\/?$/.test(window.location.pathname) && sessionStorage.getItem('account')) {
                            extendSession_IB();
                        }
                    }
                },
                10 * 60 * 1000 // 10 min
            );
        }

        const jwtDecodeStr = jwtDecode(accessTokens);
        const sid = await getEwinSessionID();
        window.sessionStorage.setItem('session_id', sid);
        window.sessionStorage.setItem('account', jwtDecodeStr.betAcct);
        window.sessionStorage.setItem('gu_id', '');
        window.sessionStorage.setItem('sso_guid', '');

        await checkNewCIAMJwt(accessTokens, setLoginAPILoading);

        return accessTokens;
    };

    const FR_GetToken = async () => {
        let step = currentStep;
        const token = await refreshToken();

        setJwtToken(token);

        return token;
    };

    const refreshToken = async (token) => {
        //authorize token
        let tokens = null;

        if(window.sessionStorage.getItem('refresh_token')) {

            const payload = new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: window.sessionStorage.getItem('refresh_token'),
                client_id: tempConfig.WEB_OAUTH_CLIENT,
            });

           const response = await fetch(window.globalConfig.ARKLE_URL + "/oauth2/realms/root/realms/customer/access_token", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'API-Version': 3,
                    'app-trace-name': 'JCBW',
                    'app-trace-id': localStorage.getItem('app-trace-id')
                },
                body: payload.toString()
            })
            const responseJson = await response.json();
            if(responseJson) {
                tokens = {
                    'accessToken': responseJson.access_token,
                    'idToken': responseJson.id_token,
                    'refreshToken': responseJson.refresh_token
                }
            }
        } else {

            tokens = await forgerock.TokenManager.getTokens({
                forceRenew: true,
                login: 'embedded',
                csrf: token,
                decision: 'allow',
                logger: (l) => console.debug('get token', l)
            });  
        }
        const accessTokens = tokens.accessToken;
        const idTokens = tokens.idToken;
        const refreshTokens = tokens.refreshToken;

        if (!accessTokens || !idTokens || !refreshTokens) {
            console.error('Failed to retrieve tokens');
        } else {
            window.sessionStorage.setItem('access_token', accessTokens);
            window.sessionStorage.setItem('id_token', idTokens);
            window.sessionStorage.setItem('refresh_token', refreshTokens);
            document.cookie =
                'access_token=' +
                accessTokens +
                '; domain=' +
                window.location.hostname.split('.').slice(-2).join('.') +
                ';path=/';
            
            setJwtToken(accessTokens);
            setTokenToSession(tokens);
            if (window.globalConfig.arkleProfiles && idTokens) {
                setCustomerProfileDataFromTokens(accessTokens, idTokens);
            }

            console.debug('Tokens refreshed successfully');
        }

        return accessTokens;
    };

    const FR_Logout = async () => {
        // Check for tokens first; end session and revoke and delete if present
        try {
            const tokenStorage = await TokenStorage.get();
            forgerock.SessionManager.logout(); //for clearing Arkle RisingSun cookie

            if (tokenStorage) {
                try {
                    // Revoke tokens on server
                    await OAuth2Client.endSession();
                    await OAuth2Client.revokeToken();
                } catch (error) {
                    // Log and continue
                    console.warn('revokeToken and endSession were not successful');
                }
                // Locally remove tokens
                await TokenStorage.remove();
            }

            //clear refresh token interval
            clearInterval(window.refreshSessionIntervalId);
            window.refreshSessionIntervalId = undefined;
        } catch (error) {
            console.error('Error during logout:', error);
            await forgerock.FRUser.logout(); //try use old method to logout in case of error
        }
    };

    const FR_NewEKBA = async () => {
        try {
            console.debug('NEW EKBA FR_initStep...');

            let step = await next(null);
            const choiceCallback = step.getCallbackOfType('ChoiceCallback');
            //0 sso
            choiceCallback.setChoiceIndex(0);
            const rsp = await next(step);
            setCurrentStep(rsp);
            handleConfirmTrustBrowser(rsp);
            return rsp;
        } catch (error) {
            console.debug('Error in NewEKBA', error);
        }
    };
    const setCIAMLoginIncorrectMsg = (error) => {
        if (error?.length > 0) {
            setLoginIncorrectMsg(`CIAM_${error}`);
        } else {
            setLoginIncorrectMsg('');
        }
    };

    const FR_GetMobile = () => {
        return mobile;
    };

    return {
        FR_initStep,
        FR_loginWithUsernamePassword,
        FR_loginWithOTP,
        FR_loginWithPIN,
        FR_TurstBrowser,
        FR_GetToken,
        refreshToken,
        isShowOTPAuth,
        setIsShowOTPAuth,
        isShowSecurityPinAuth,
        setIsShowSecurityPinAuth,
        isMissingMobile,
        setIsMissingMobile,
        invalidOTP,
        setInvalidOTP,
        invalidPIN,
        setInvalidPIN,
        pwd,
        setPwd,
        loginIncorrectMsg,
        setLoginIncorrectMsg,
        closeLoginIncorrectMsg,
        setIsShowTrustBrowser,
        isShowTrustBrowser,
        jwtToken,
        FR_Logout,
        FR_GetMobile,
        FR_ResendOTP,
        showErrorPopup,
        setShowErrorPopup,
        showLoginLockPopup,
        setShowLoginLockPopup,
        isShowOTPToast,
        setIsShowOTPToast,
        isVoiceOTP,
        setIsVoiceOTP,
        isSecurityPin,
        setIsSecurityPin,
        FR_NewEKBA,
        isPassKeyRegConsent,
        setIsPassKeyRegConsent,
        FR_passKeyBindProcess,
        isPassKeyRegSuccess,
        setIsPassKeyRegSuccess,
        FR_passKeySuccess
    };
};
